import { useState, useMemo, useEffect } from "react";
import { useSearchQueryParam } from "@/hooks/use-search-query-param";
import { matchesSearch } from "@/lib/global-search";
import { useLocation, useSearch } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, isUpgradeRequiredError } from "@/lib/queryClient";
import { InlineUpgradePanel } from "@/components/inline-upgrade-panel";
import { buildAffiliateLink } from "@shared/affiliate-links";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertSupplyItemSchema, insertSupplyOrderSchema, insertSupplyRecurringScheduleSchema, SUPPLY_CATEGORIES, SUPPLY_ORDER_STATUSES, type SupplyItem, type SupplyOrder, type SupplyRecurringSchedule, type Property, type TeamMember, type SupplyPropertySettings } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { z } from "zod";
import { Plus, ExternalLink, Building2, Package, ShoppingCart, Clock, AlertTriangle, Star, Repeat, Trash2, Edit, ChevronDown, ChevronUp, Search, Settings, MapPin, ChevronsUpDown, Check, Archive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInDays, isPast } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { BackToPropertyLink } from "@/components/back-to-property-link";

const STATUS_COLORS: Record<string, string> = {
  requested: "bg-yellow-100 text-yellow-800",
  ordered: "bg-blue-100 text-blue-800",
  delivered: "bg-green-100 text-green-800",
};

const createItemSchema = insertSupplyItemSchema.omit({ userId: true });
type CreateItemForm = z.infer<typeof createItemSchema>;

const createOrderSchema = insertSupplyOrderSchema.omit({ userId: true }).extend({
  propertyId: z.number({ required_error: "Property is required" }).min(1, "Property is required"),
  dateRequested: z.string().optional(),
  dateOrdered: z.string().optional().nullable(),
  dateDelivered: z.string().optional().nullable(),
});
type CreateOrderForm = z.infer<typeof createOrderSchema>;

const createScheduleSchema = insertSupplyRecurringScheduleSchema.omit({ userId: true }).extend({
  lastOrderDate: z.string().optional().nullable(),
  nextDueDate: z.string().min(1, "Next due date is required"),
  intervalDays: z.number({ required_error: "Interval is required", invalid_type_error: "Interval is required" }).min(1, "Interval must be at least 1 day"),
});
type CreateScheduleForm = z.infer<typeof createScheduleSchema>;

interface OrderHistory {
  orders: SupplyOrder[];
  avgIntervalDays: number | null;
  anomaly: boolean;
  totalOrders: number;
}

export default function SuppliesView({ propertyId: externalPropertyId }: { propertyId?: string | null }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea, isOwner } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();

  const [pathname, navigate] = useLocation();
  const urlSearch = useSearch();
  const params = new URLSearchParams(urlSearch);
  const initialPropertyId = externalPropertyId || params.get("propertyId");

  const [activeTab, setActiveTab] = useTabQueryParam("tab", "orders", {
    tabs: ["orders", "catalog", "recurring", "settings"],
  });
  const propertyFilter = externalPropertyId || params.get("propertyId") || "all";
  const setPropertyFilter = (v: string) => {
    if (externalPropertyId) return;
    const next = new URLSearchParams(urlSearch);
    if (v === "all") next.delete("propertyId");
    else next.set("propertyId", v);
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };
  const [categoryFilter, setCategoryFilterState] = useState<string>(
    () => localStorage.getItem("pp:supplies-category-filter") ?? "all",
  );
  const setCategoryFilter = (next: string) => {
    localStorage.setItem("pp:supplies-category-filter", next);
    setCategoryFilterState(next);
  };
  const [statusFilter, setStatusFilterState] = useState<string>(
    () => localStorage.getItem("pp:supplies-status-filter") ?? "all",
  );
  const setStatusFilter = (next: string) => {
    localStorage.setItem("pp:supplies-status-filter", next);
    setStatusFilterState(next);
  };
  const [searchText, setSearchText, incomingSearch] = useSearchQueryParam();
  useEffect(() => {
    if (incomingSearch === null) return;
    setCategoryFilterState("all");
    setStatusFilterState("all");
  }, [incomingSearch]);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [createScheduleOpen, setCreateScheduleOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SupplyItem | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<SupplyOrder | null>(null);
  const [markingOrdered, setMarkingOrdered] = useState(false);
  const [markOrderedDate, setMarkOrderedDate] = useState("");
  const [markOrderedReference, setMarkOrderedReference] = useState("");
  const [markTrackingUrl, setMarkTrackingUrl] = useState("");
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [markDeliveredDate, setMarkDeliveredDate] = useState("");
  const [markDeliveredTracking, setMarkDeliveredTracking] = useState("");
  const [historyItemId, setHistoryItemId] = useState<number | null>(null);
  const [historyPropertyId, setHistoryPropertyId] = useState<number | null>(null);

  const [orderItemFreeform, setOrderItemFreeform] = useState(false);
  const [orderSaveToCatalog, setOrderSaveToCatalog] = useState(false);
  const [orderComboboxOpen, setOrderComboboxOpen] = useState(false);
  const [orderCatalogSearch, setOrderCatalogSearch] = useState("");

  const [quickRequestItem, setQuickRequestItem] = useState<SupplyItem | null>(null);
  const [quickRequestPropertyId, setQuickRequestPropertyId] = useState<string>("");
  const [quickRequestMember, setQuickRequestMember] = useState<string>("");
  const [deletingItem, setDeletingItem] = useState<SupplyItem | null>(null);

  const itemsQuery = useQuery<SupplyItem[]>({ queryKey: ["/api/app/supply-items"] });
  const ordersQuery = useQuery<SupplyOrder[]>({ queryKey: ["/api/app/supply-orders"] });
  const schedulesQuery = useQuery<SupplyRecurringSchedule[]>({ queryKey: ["/api/app/supply-recurring-schedules"] });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const settingsQuery = useQuery<SupplyPropertySettings[]>({ queryKey: ["/api/app/supply-property-settings"] });
  const profileQuery = useQuery<{ id: number; firstName: string | null; lastName: string | null; email: string }>({ queryKey: ["/api/app/profile"] });
  const membersQuery = useQuery<Array<{ id: number; name: string; displayName: string | null; currentPropertyId: number | null }>>({ queryKey: ["/api/app/devices/members"] });
  const categoriesQuery = useQuery<{ id: number; name: string; isDefault: boolean }[]>({ queryKey: ["/api/app/supply-categories"] });

  const historyQuery = useQuery<OrderHistory>({
    queryKey: ["/api/app/supply-orders", historyItemId, "history", { propertyId: historyPropertyId }],
    queryFn: async () => {
      const url = historyPropertyId
        ? `/api/app/supply-orders/${historyItemId}/history?propertyId=${historyPropertyId}`
        : `/api/app/supply-orders/${historyItemId}/history`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch history");
      return res.json();
    },
    enabled: historyItemId !== null,
  });

  const items = itemsQuery.data || [];
  const orders = ordersQuery.data || [];
  const journeyStage = useJourneyStage();
  const schedules = schedulesQuery.data || [];
  const allProperties = propertiesQuery.data || [];
  const properties = sortProperties(allProperties.filter((p) => p.status === "active"));
  const teamMembers = (teamQuery.data || []).filter((m) => m.status === "accepted");
  const assignableTeamMembers = teamMembers.filter((m) => m.inviteeUserId != null);
  const propertySettings = settingsQuery.data || [];
  // The account owner is always a selectable assignee and is the default
  // auto-assignee for every property unless an explicit per-property setting
  // overrides it — otherwise supply requests would have no one to notify.
  const ownerAssigneeId = isOwner ? (profileQuery.data?.id ?? null) : null;
  const ownerName = profileQuery.data
    ? ([profileQuery.data.firstName, profileQuery.data.lastName].filter(Boolean).join(" ").trim() || profileQuery.data.email)
    : "Account owner";
  const assigneeDisplayName = (assigneeId: number | null | undefined): string | null => {
    if (assigneeId == null) return null;
    if (ownerAssigneeId != null && assigneeId === ownerAssigneeId) return ownerName;
    const m = assignableTeamMembers.find((tm) => tm.inviteeUserId === assigneeId);
    return m ? (m.name || m.email) : null;
  };
  const customCategories = categoriesQuery.data || [];

  const allCategories = useMemo(() => {
    const defaultSet = new Set<string>(SUPPLY_CATEGORIES);
    const customNames = customCategories.map(c => c.name);
    const merged = [...SUPPLY_CATEGORIES, ...customNames.filter(n => !defaultSet.has(n))];
    return merged.sort();
  }, [customCategories]);

  const [editingOrder, setEditingOrder] = useState<SupplyOrder | null>(null);

  const twelveMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return d;
  }, []);

  const filteredOrders = useMemo(() => {
    let result = orders.filter((o) => new Date(o.dateRequested) >= twelveMonthsAgo);
    if (propertyFilter !== "all") result = result.filter((o) => o.propertyId === parseInt(propertyFilter, 10));
    if (categoryFilter !== "all") result = result.filter((o) => o.category === categoryFilter);
    if (statusFilter !== "all") result = result.filter((o) => o.status === statusFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter((o) => String(o.itemName ?? "").toLowerCase().includes(q) || String(o.notes ?? "").toLowerCase().includes(q));
    }
    return result;
  }, [orders, propertyFilter, categoryFilter, statusFilter, searchText, twelveMonthsAgo]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter !== "all") result = result.filter((i) => i.category === categoryFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter((i) => matchesSearch(q, [i.name, i.category, i.preferredVendor, "supply supplies catalog"]));
    }
    return result;
  }, [items, categoryFilter, searchText]);

  const frequentItems = useMemo(() => items.filter((i) => i.isFrequent), [items]);

  const dueSchedules = useMemo(() => {
    return schedules.filter((s) => !s.archivedAt && s.isActive && s.nextDueDate && isPast(new Date(s.nextDueDate)));
  }, [schedules]);

  const activeSchedules = useMemo(() => schedules.filter((s) => !s.archivedAt), [schedules]);

  const selectedPropertyName = useMemo(() => {
    if (propertyFilter === "all") return `All Properties (${properties.length})`;
    const p = properties.find((pr) => pr.id === parseInt(propertyFilter, 10));
    return p ? p.name : "Property";
  }, [propertyFilter, properties]);

  const orderForm = useForm<CreateOrderForm, any, CreateOrderForm>({
    resolver: zodResolver(createOrderSchema),
    defaultValues: { itemName: "", category: null, propertyId: initialPropertyId ? parseInt(initialPropertyId, 10) : undefined, requestedBy: "", assigneeId: null, status: "requested", quantity: 1, purchaseUrl: null, notes: null, supplyItemId: null },
  });

  const itemForm = useForm<CreateItemForm, any, CreateItemForm>({
    resolver: zodResolver(createItemSchema),
    defaultValues: { name: "", category: "General", purchaseUrl: null, preferredVendor: null, defaultQuantity: 1, isFrequent: false },
  });

  const scheduleForm = useForm<CreateScheduleForm, any, CreateScheduleForm>({
    resolver: zodResolver(createScheduleSchema),
    defaultValues: { supplyItemId: 0, propertyId: undefined, intervalDays: 30, lastOrderDate: null, nextDueDate: format(new Date(), "yyyy-MM-dd"), isActive: true },
  });

  function resetOrderDialog() {
    setOrderItemFreeform(false);
    setOrderSaveToCatalog(false);
    setOrderComboboxOpen(false);
    setOrderCatalogSearch("");
  }

  const createOrderMutation = useMutation({
    mutationFn: async (data: CreateOrderForm) => {
      const res = await apiRequest("POST", "/api/app/supply-orders", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-recurring-schedules"] });
      setCreateOrderOpen(false);
      orderForm.reset();
      resetOrderDialog();
      toast({ title: "Supply order created" });
    },
    onError: () => toast({ title: "Failed to create order", variant: "destructive" }),
  });

  async function handleSubmitOrder(data: CreateOrderForm) {
    try {
      await createOrderMutation.mutateAsync(data);
      if (orderItemFreeform && orderSaveToCatalog) {
        try {
          await apiRequest("POST", "/api/app/supply-items", {
            name: data.itemName,
            category: data.category || "General",
            purchaseUrl: data.purchaseUrl || null,
            defaultQuantity: data.quantity || 1,
            isFrequent: false,
          });
          queryClient.invalidateQueries({ queryKey: ["/api/app/supply-items"] });
          toast({ title: "Item saved to catalog" });
        } catch {
          toast({ title: "Order created, but failed to save item to catalog", variant: "destructive" });
        }
      }
    } catch {
      // error handled by mutation
    }
  }

  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/app/supply-orders/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-recurring-schedules"] });
      if (selectedOrder) setSelectedOrder(null);
      if (editingOrder) setEditingOrder(null);
      toast({ title: "Order updated" });
    },
    onError: () => toast({ title: "Failed to update order", variant: "destructive" }),
  });

  const deleteOrderMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/supply-orders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-orders"] });
      setSelectedOrder(null);
      toast({ title: "Order deleted" });
    },
  });

  const createItemMutation = useMutation({
    mutationFn: async (data: CreateItemForm) => {
      const res = await apiRequest("POST", "/api/app/supply-items", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-items"] });
      setCreateItemOpen(false);
      itemForm.reset();
      toast({ title: "Supply item created" });
    },
    onError: () => toast({ title: "Failed to create item", variant: "destructive" }),
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<SupplyItem> }) => {
      const res = await apiRequest("PATCH", `/api/app/supply-items/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-items"] });
      setEditingItem(null);
      toast({ title: "Item updated" });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/supply-items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-items"] });
      setDeletingItem(null);
      toast({ title: "Item deleted" });
    },
    onError: (err) => toast({ title: "Failed to delete item", description: parseErrorMessage(err, "Couldn't delete this item. Please try again."), variant: "destructive" }),
  });

  const createScheduleMutation = useMutation({
    mutationFn: async (data: CreateScheduleForm) => {
      const res = await apiRequest("POST", "/api/app/supply-recurring-schedules", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-recurring-schedules"] });
      setCreateScheduleOpen(false);
      scheduleForm.reset();
      toast({ title: "Recurring schedule created" });
    },
    onError: () => toast({ title: "Failed to create schedule", variant: "destructive" }),
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/supply-recurring-schedules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-recurring-schedules"] });
      toast({ title: "Schedule deleted" });
    },
  });

  const upsertSettingsMutation = useMutation({
    mutationFn: async (data: { propertyId: number; defaultAssigneeId: number | null }) => {
      const res = await apiRequest("POST", "/api/app/supply-property-settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-property-settings"] });
      toast({ title: "Settings saved" });
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  const quickRequestMutation = useMutation({
    mutationFn: async ({ item, propertyId, requestedBy }: { item: SupplyItem; propertyId: number; requestedBy: string | null }) => {
      const assigneeId = propertySettings.find((s) => s.propertyId === propertyId)?.defaultAssigneeId ?? null;
      const res = await apiRequest("POST", "/api/app/supply-orders", {
        itemName: item.name,
        category: item.category,
        supplyItemId: item.id,
        purchaseUrl: item.purchaseUrl,
        quantity: item.defaultQuantity || 1,
        propertyId,
        assigneeId,
        status: "requested",
        requestedBy,
        notes: null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-orders"] });
      setQuickRequestItem(null);
      setQuickRequestPropertyId("");
      setQuickRequestMember("");
      toast({ title: "Supply request created" });
    },
    onError: (err) => toast({ title: "Failed to create request", description: parseErrorMessage(err, "Couldn't submit your request. Please try again."), variant: "destructive" }),
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/app/supply-categories", { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-categories"] });
      toast({ title: "Category added" });
    },
    onError: () => toast({ title: "Failed to add category", variant: "destructive" }),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/supply-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-categories"] });
      toast({ title: "Category removed" });
    },
    onError: () => toast({ title: "Failed to remove category", variant: "destructive" }),
  });

  const getDefaultAssignee = (propId: number | null | undefined): number | null => {
    if (!propId) return null;
    const setting = propertySettings.find((s) => s.propertyId === propId);
    if (setting) return setting.defaultAssigneeId ?? null;
    return ownerAssigneeId;
  };

  const orderFormPropertyId = orderForm.watch("propertyId");
  const propertyMembers = (membersQuery.data || []).filter((m) => m.currentPropertyId === orderFormPropertyId);

  const handlePropertyChange = (propId: number | null) => {
    orderForm.setValue("propertyId", propId as number);
    orderForm.setValue("requestedBy", null);
    const defaultAssignee = getDefaultAssignee(propId);
    if (defaultAssignee) {
      orderForm.setValue("assigneeId", defaultAssignee);
    }
  };

  const handleEditPropertyChange = (propId: number | null) => {
    if (!editingOrder) return;
    setEditingOrder({ ...editingOrder, propertyId: propId });
    const defaultAssignee = getDefaultAssignee(propId);
    if (defaultAssignee) {
      setEditingOrder((prev) => prev ? { ...prev, propertyId: propId, assigneeId: defaultAssignee } : null);
    }
  };

  const updateScheduleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<SupplyRecurringSchedule> }) => {
      const res = await apiRequest("PATCH", `/api/app/supply-recurring-schedules/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/supply-recurring-schedules"] });
      toast({ title: "Schedule updated" });
    },
  });

  function quickOrderFromItem(item: SupplyItem) {
    orderForm.reset({
      itemName: item.name,
      category: item.category,
      supplyItemId: item.id,
      purchaseUrl: item.purchaseUrl,
      quantity: item.defaultQuantity || 1,
      propertyId: initialPropertyId ? parseInt(initialPropertyId, 10) : undefined,
      requestedBy: "",
      assigneeId: null,
      status: "requested",
      notes: null,
    });
    resetOrderDialog();
    setCreateOrderOpen(true);
  }

  if (isUpgradeRequiredError(ordersQuery.error) || isUpgradeRequiredError(itemsQuery.error)) {
    return <InlineUpgradePanel error={ordersQuery.error ?? itemsQuery.error} />;
  }

  return (
    <>
      <div className="p-6 h-full flex flex-col">
        <ViewOnlyBanner area="maintenance" />
        {initialPropertyId ? (
          <BackToPropertyLink propertyId={initialPropertyId} tab="supplies" />
        ) : null}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-supplies-title" data-tour="supplies-heading">Supplies</h1>
            <SearchableSelect
              value={propertyFilter}
              onValueChange={setPropertyFilter}
              placeholder={selectedPropertyName}
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId="select-supply-property-filter"
              className="w-full sm:w-auto gap-2 border-gray-300 h-9 px-3 min-w-0"
              leadingIcon={<Building2 className="h-4 w-4 text-gray-500" />}
              options={[
                { value: "all", label: `All Properties (${properties.length})` },
                ...properties.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                })),
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "orders" && (
              <Button disabled={readOnly} size="sm" onClick={() => { orderForm.reset(); setCreateOrderOpen(true); }} data-testid="button-create-supply-order" data-tour="supplies-add">
                <Plus className="h-4 w-4 mr-1.5" />
                Request Supply
              </Button>
            )}
            {activeTab === "catalog" && (
              <Button disabled={readOnly} size="sm" onClick={() => { itemForm.reset(); setCreateItemOpen(true); }} data-testid="button-create-supply-item">
                <Plus className="h-4 w-4 mr-1.5" />
                Add Item
              </Button>
            )}
            {activeTab === "recurring" && (
              <Button disabled={readOnly} size="sm" onClick={() => { scheduleForm.reset({ supplyItemId: items[0]?.id || 0, propertyId: undefined, intervalDays: 30, lastOrderDate: null, nextDueDate: format(new Date(), "yyyy-MM-dd"), isActive: true }); setCreateScheduleOpen(true); }} data-testid="button-create-schedule">
                <Plus className="h-4 w-4 mr-1.5" />
                Add Schedule
              </Button>
            )}
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="supplies" />
              <GuideButton pageKey="supplies" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="supplies" />
        </div>

        {dueSchedules.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2" data-testid="banner-due-schedules">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-800">
              {dueSchedules.length} supply {dueSchedules.length === 1 ? "item is" : "items are"} due for reorder!
            </span>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="mb-4 border-b w-full justify-start rounded-none bg-transparent p-0 overflow-x-auto" data-testid="tabs-supplies">
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-supply-orders">
              <ShoppingCart className="h-4 w-4 mr-1.5" />
              Orders ({filteredOrders.length})
            </TabsTrigger>
            <TabsTrigger value="catalog" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-supply-catalog">
              <Package className="h-4 w-4 mr-1.5" />
              Catalog ({items.length})
            </TabsTrigger>
            <TabsTrigger value="recurring" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-supply-recurring">
              <Repeat className="h-4 w-4 mr-1.5" />
              Recurring ({activeSchedules.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-supply-settings">
              <Settings className="h-4 w-4 mr-1.5" />
              Settings
            </TabsTrigger>
          </TabsList>

          <div className="mb-4">
            <button className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline mb-3" onClick={() => setFiltersOpen(!filtersOpen)} data-testid="button-toggle-supply-filters">
              {filtersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Filter
            </button>
            {filtersOpen && (
              <div className="flex items-center gap-3 flex-wrap" data-testid="supply-filter-bar">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[180px] h-9 border-gray-300" data-testid="select-supply-category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {allCategories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeTab === "orders" && (
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px] h-9 border-gray-300" data-testid="select-supply-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {SUPPLY_ORDER_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search..." className="w-[200px] h-9 border-gray-300 pl-9" data-testid="input-supply-search" />
                </div>
              </div>
            )}
          </div>

          <TabsContent value="orders" className="flex-1 overflow-auto mt-0">
            {ordersQuery.isLoading ? (
              <div className="text-center py-12 text-gray-400">Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              orders.length === 0 ? (
                <JourneyEmptyState
                  page="supplies"
                  stage={journeyStage}
                  icon={<ShoppingCart className="h-10 w-10 text-gray-300" />}
                  primaryAction={{ onClick: () => { orderForm.reset(); setCreateOrderOpen(true); } }}
                  testIdPrefix="empty-supplies"
                />
              ) : (
                <div className="text-center py-12" data-testid="empty-supplies-filtered">
                  <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No supply orders match your filters</p>
                </div>
              )
            ) : (
              <div className="border rounded-lg overflow-x-auto" data-testid="table-supply-orders" data-tour="supplies-orders">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-600">Item</th>
                      <th className="text-left p-3 font-medium text-gray-600">Property</th>
                      <th className="text-left p-3 font-medium text-gray-600">Status</th>
                      <th className="text-left p-3 font-medium text-gray-600">Requested By</th>
                      <th className="text-left p-3 font-medium text-gray-600">Assignee</th>
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => {
                      const prop = order.propertyId ? properties.find((p) => p.id === order.propertyId) : null;
                      const assigneeLabel = assigneeDisplayName(order.assigneeId);
                      return (
                        <tr key={order.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedOrder(order)} data-testid={`row-supply-order-${order.id}`}>
                          <td className="p-3">
                            <div className="font-medium text-gray-900" data-testid={`text-order-item-${order.id}`}>{order.itemName}</div>
                            {order.category && <div className="text-xs text-gray-500">{order.category}</div>}
                          </td>
                          <td className="p-3 text-gray-600">{prop?.name || "—"}</td>
                          <td className="p-3">
                            <Badge className={STATUS_COLORS[order.status]} data-testid={`badge-order-status-${order.id}`}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </Badge>
                          </td>
                          <td className="p-3 text-gray-600">{order.requestedBy || "—"}</td>
                          <td className="p-3 text-gray-600">{assigneeLabel || "—"}</td>
                          <td className="p-3 text-gray-500 text-xs">{format(new Date(order.dateRequested), "MMM d, yyyy")}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              {order.trackingUrl && (
                                <a href={order.trackingUrl.startsWith("http") ? order.trackingUrl : `https://${order.trackingUrl}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800" title="Tracking link" data-testid={`link-tracking-${order.id}`}>
                                  <Package className="h-4 w-4" />
                                </a>
                              )}
                              {order.supplyItemId && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setHistoryItemId(order.supplyItemId!); setHistoryPropertyId(order.propertyId || null); }} data-testid={`button-history-${order.id}`}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  History
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="catalog" className="flex-1 overflow-auto mt-0">
            {frequentItems.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-amber-500" />
                  Frequent Items
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="grid-frequent-items">
                  {frequentItems.map((item) => (
                    <div key={item.id} className="border rounded-lg p-3 hover:shadow-sm transition-shadow" data-testid={`card-frequent-item-${item.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 text-sm">{item.name}</span>
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <Badge variant="outline" className="text-xs">{item.category}</Badge>
                        {item.preferredVendor && <span>{item.preferredVendor}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setQuickRequestItem(item); setQuickRequestPropertyId(initialPropertyId || ""); setQuickRequestMember(""); }} data-testid={`button-quick-request-${item.id}`}>
                          Request
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {itemsQuery.isLoading ? (
              <div className="text-center py-12 text-gray-400">Loading catalog...</div>
            ) : filteredItems.length === 0 ? (
              items.length === 0 ? (
                <JourneyEmptyState
                  page="supplies_catalog"
                  stage={journeyStage}
                  icon={<Package className="h-10 w-10 text-gray-300" />}
                  primaryAction={{ onClick: () => { itemForm.reset(); setCreateItemOpen(true); } }}
                  testIdPrefix="empty-supplies-catalog"
                />
              ) : (
                <div className="text-center py-12" data-testid="empty-supplies-catalog-filtered">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No supply items match your filters</p>
                </div>
              )
            ) : (
              <div className="border rounded-lg overflow-x-auto" data-testid="table-supply-items">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-600">Name</th>
                      <th className="text-left p-3 font-medium text-gray-600">Category</th>
                      <th className="text-left p-3 font-medium text-gray-600">Vendor</th>
                      <th className="text-left p-3 font-medium text-gray-600">Qty</th>
                      <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`row-supply-item-${item.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{item.name}</span>
                            {item.isFrequent && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                          </div>
                        </td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{item.category}</Badge></td>
                        <td className="p-3 text-gray-600">{item.preferredVendor || "—"}</td>
                        <td className="p-3 text-gray-600">{item.defaultQuantity}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setQuickRequestItem(item); setQuickRequestPropertyId(initialPropertyId || ""); setQuickRequestMember(""); }} data-testid={`button-request-item-${item.id}`}>
                              Request
                            </Button>
                            <Button disabled={readOnly} variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingItem(item)} data-testid={`button-edit-item-${item.id}`}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button disabled={readOnly} variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={() => setDeletingItem(item)} data-testid={`button-delete-item-${item.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="recurring" className="flex-1 overflow-auto mt-0">
            {schedulesQuery.isLoading ? (
              <div className="text-center py-12 text-gray-400">Loading schedules...</div>
            ) : schedules.length === 0 ? (
              <JourneyEmptyState
                page="supplies_recurring"
                stage={journeyStage}
                icon={<Repeat className="h-10 w-10 text-gray-300" />}
                primaryAction={{ onClick: () => { scheduleForm.reset({ supplyItemId: items[0]?.id || 0, propertyId: undefined, intervalDays: 30, lastOrderDate: null, nextDueDate: format(new Date(), "yyyy-MM-dd"), isActive: true }); setCreateScheduleOpen(true); } }}
                testIdPrefix="empty-supplies-recurring"
              />
            ) : (
              <div className="border rounded-lg overflow-x-auto" data-testid="table-recurring-schedules">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium w-20">Enabled</th>
                      <th className="text-left p-3 font-medium">Supply Item</th>
                      <th className="text-left p-3 font-medium">Property</th>
                      <th className="text-left p-3 font-medium">Interval</th>
                      <th className="text-left p-3 font-medium">Next Date</th>
                      <th className="text-right p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSchedules.map((schedule) => {
                      const item = items.find((i) => i.id === schedule.supplyItemId);
                      const prop = schedule.propertyId ? properties.find((p) => p.id === schedule.propertyId) : null;
                      const isDue = schedule.isActive && schedule.nextDueDate && isPast(new Date(schedule.nextDueDate));
                      return (
                        <tr key={schedule.id} className={`border-t ${isDue ? "bg-amber-50" : ""}`} data-testid={`row-schedule-${schedule.id}`}>
                          <td className="p-3">
                            <Switch disabled={readOnly} checked={schedule.isActive ?? true} onCheckedChange={(checked) => updateScheduleMutation.mutate({ id: schedule.id, data: { isActive: checked } })} data-testid={`switch-schedule-active-${schedule.id}`} />
                          </td>
                          <td className="p-3">
                            <span className="font-medium text-gray-900" data-testid={`text-schedule-item-${schedule.id}`}>{item?.name || "Unknown Item"}</span>
                          </td>
                          <td className="p-3 text-gray-600" data-testid={`text-schedule-property-${schedule.id}`}>{prop?.name || "—"}</td>
                          <td className="p-3 text-gray-600" data-testid={`text-schedule-interval-${schedule.id}`}>Every {schedule.intervalDays} days</td>
                          <td className="p-3" data-testid={`text-schedule-next-${schedule.id}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-700">{schedule.nextDueDate ? format(new Date(schedule.nextDueDate), "MMM d, yyyy") : "—"}</span>
                              {isDue && <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid={`badge-due-${schedule.id}`}>Due</Badge>}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button disabled={readOnly} size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (item) quickOrderFromItem(item); }} data-testid={`button-schedule-order-${schedule.id}`}>
                                <ShoppingCart className="h-3 w-3 mr-1" />
                                Order Now
                              </Button>
                              <Button disabled={readOnly || updateScheduleMutation.isPending} variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={() => updateScheduleMutation.mutate({ id: schedule.id, data: { archivedAt: new Date() } })} data-testid={`button-archive-schedule-${schedule.id}`}>
                                <Archive className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="flex-1 overflow-auto mt-0">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Auto-Assign Defaults</h3>
                <p className="text-sm text-gray-500 mb-4">Set a default team member to be auto-assigned when creating supply requests for each property.</p>
              </div>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Property</th>
                      <th className="text-left p-3 font-medium">Default Assignee</th>
                      <th className="p-3 w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((p) => {
                      const setting = propertySettings.find((s) => s.propertyId === p.id);
                      const currentAssignee = setting ? (setting.defaultAssigneeId ?? null) : ownerAssigneeId;
                      return (
                        <tr key={p.id} className="border-t" data-testid={`row-setting-${p.id}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-gray-400" />
                              <span className="font-medium">{p.name}</span>
                            </div>
                            {p.address && <p className="text-xs text-gray-400 ml-6">{p.address}</p>}
                          </td>
                          <td className="p-3">
                            <Select
                              value={currentAssignee != null ? String(currentAssignee) : "none"}
                              onValueChange={(v) => {
                                upsertSettingsMutation.mutate({ propertyId: p.id, defaultAssigneeId: v === "none" ? null : parseInt(v, 10) });
                              }}
                            >
                              <SelectTrigger className="w-48" data-testid={`select-default-assignee-${p.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No default</SelectItem>
                                {ownerAssigneeId != null && <SelectItem value={String(ownerAssigneeId)}>{ownerName} (Account owner)</SelectItem>}
                                {assignableTeamMembers.map((m) => <SelectItem key={m.id} value={String(m.inviteeUserId)}>{m.name || m.email}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3 text-center">
                            {setting ? <span className="text-xs text-green-600">Saved</span> : ownerAssigneeId != null ? <span className="text-xs text-gray-400">Default</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {properties.length === 0 && (
                <div className="text-center py-8 text-gray-400">No active properties found.</div>
              )}

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-1">Custom Categories</h3>
                <p className="text-sm text-gray-500 mb-4">Add custom supply categories beyond the defaults ({SUPPLY_CATEGORIES.join(", ")}).</p>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="New category name..."
                    data-testid="input-new-category"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = (e.target as HTMLInputElement).value.trim();
                        if (val) {
                          createCategoryMutation.mutate(val);
                          (e.target as HTMLInputElement).value = "";
                        }
                      }
                    }}
                    className="max-w-xs"
                  />
                  <Button
                    disabled={readOnly}
                    variant="outline"
                    data-testid="button-add-category"
                    onClick={() => {
                      const input = document.querySelector('[data-testid="input-new-category"]') as HTMLInputElement;
                      if (input && input.value.trim()) {
                        createCategoryMutation.mutate(input.value.trim());
                        input.value = "";
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {customCategories.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {customCategories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-3" data-testid={`row-category-${cat.id}`}>
                        <span className="text-sm font-medium">{cat.name}</span>
                        <Button disabled={readOnly}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          data-testid={`button-delete-category-${cat.id}`}
                          onClick={() => deleteCategoryMutation.mutate(cat.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {customCategories.length === 0 && (
                  <div className="text-sm text-gray-400">No custom categories yet. Default categories are always available.</div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!quickRequestItem} onOpenChange={(open) => { if (!open) { setQuickRequestItem(null); setQuickRequestPropertyId(""); setQuickRequestMember(""); } }}>
        <DialogContent className="max-w-sm" data-testid="dialog-quick-request">
          <DialogHeader><DialogTitle>Request {quickRequestItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Property</label>
              <Select value={quickRequestPropertyId} onValueChange={(v) => { setQuickRequestPropertyId(v); setQuickRequestMember(""); }} data-testid="select-quick-request-property">
                <SelectTrigger data-testid="trigger-quick-request-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {quickRequestPropertyId && (() => {
              const pid = parseInt(quickRequestPropertyId, 10);
              const quickMembers = (membersQuery.data || []).filter((m) => m.currentPropertyId === pid);
              return (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Member requested by</label>
                  <Select value={quickRequestMember || "none"} onValueChange={(v) => setQuickRequestMember(v === "none" ? "" : v)} data-testid="select-quick-request-member">
                    <SelectTrigger data-testid="trigger-quick-request-member">
                      <SelectValue placeholder="Select member (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No member</SelectItem>
                      {quickMembers.map((m) => (
                        <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            {quickRequestPropertyId && (() => {
              const pid = parseInt(quickRequestPropertyId, 10);
              const assigneeName = assigneeDisplayName(getDefaultAssignee(pid));
              return assigneeName ? (
                <p className="text-xs text-gray-500">Assignee: <span className="font-medium text-gray-700">{assigneeName}</span></p>
              ) : null;
            })()}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => { setQuickRequestItem(null); setQuickRequestPropertyId(""); setQuickRequestMember(""); }} data-testid="button-quick-request-cancel">Cancel</Button>
              <Button
                className="flex-1"
                disabled={readOnly || (!quickRequestPropertyId || quickRequestMutation.isPending)}
                onClick={() => { if (quickRequestItem && quickRequestPropertyId) quickRequestMutation.mutate({ item: quickRequestItem, propertyId: parseInt(quickRequestPropertyId, 10), requestedBy: quickRequestMember || null }); }}
                data-testid="button-quick-request-confirm"
              >
                {quickRequestMutation.isPending ? "Requesting..." : "Confirm Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createOrderOpen} onOpenChange={(open) => { setCreateOrderOpen(open); if (!open) { resetOrderDialog(); orderForm.reset(); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden" data-testid="dialog-create-supply-order">
          <DialogHeader className="flex-shrink-0"><DialogTitle>Request Supply</DialogTitle></DialogHeader>
          <Form {...orderForm}>
            <form onSubmit={orderForm.handleSubmit(handleSubmitOrder)} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              {!orderItemFreeform ? (
                <FormField control={orderForm.control} name="itemName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item Name</FormLabel>
                    <Popover open={orderComboboxOpen} onOpenChange={setOrderComboboxOpen} modal={true}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={orderComboboxOpen}
                            className="w-full justify-between font-normal"
                            data-testid="combobox-order-item-name"
                          >
                            <span className={field.value ? "text-foreground" : "text-muted-foreground"}>
                              {field.value || "Search catalog..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder="Search items..."
                            value={orderCatalogSearch}
                            onValueChange={setOrderCatalogSearch}
                            data-testid="input-catalog-search"
                          />
                          <CommandList>
                            <CommandEmpty>No items found in catalog.</CommandEmpty>
                            <CommandGroup>
                              {items
                                .filter((i) => !orderCatalogSearch || String(i.name ?? "").toLowerCase().includes(orderCatalogSearch.toLowerCase()))
                                .map((item) => (
                                  <CommandItem
                                    key={item.id}
                                    value={item.name}
                                    onSelect={() => {
                                      orderForm.setValue("itemName", item.name);
                                      orderForm.setValue("category", item.category);
                                      orderForm.setValue("purchaseUrl", item.purchaseUrl || null);
                                      orderForm.setValue("quantity", item.defaultQuantity || 1);
                                      orderForm.setValue("supplyItemId", item.id);
                                      setOrderComboboxOpen(false);
                                      setOrderCatalogSearch("");
                                    }}
                                    data-testid={`catalog-item-${item.id}`}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${orderForm.watch("supplyItemId") === item.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{item.name}</span>
                                    {item.category && <span className="ml-auto text-xs text-muted-foreground">{item.category}</span>}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                      onClick={() => {
                        setOrderItemFreeform(true);
                        orderForm.setValue("supplyItemId", null);
                        orderForm.setValue("itemName", "");
                      }}
                      data-testid="link-manual-entry"
                    >
                      Item not in catalog? Enter manually
                    </button>
                  </FormItem>
                )} />
              ) : (
                <FormField control={orderForm.control} name="itemName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Toilet Paper" data-testid="input-order-item-name" />
                    </FormControl>
                    <FormMessage />
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                        onClick={() => {
                          setOrderItemFreeform(false);
                          setOrderSaveToCatalog(false);
                          orderForm.setValue("supplyItemId", null);
                          orderForm.setValue("itemName", "");
                        }}
                        data-testid="link-back-to-catalog"
                      >
                        Back to catalog
                      </button>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="save-to-catalog"
                          checked={orderSaveToCatalog}
                          onCheckedChange={(v) => setOrderSaveToCatalog(!!v)}
                          data-testid="checkbox-save-to-catalog"
                        />
                        <label htmlFor="save-to-catalog" className="text-xs cursor-pointer select-none">
                          Save to catalog
                        </label>
                      </div>
                    </div>
                  </FormItem>
                )} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <FormField control={orderForm.control} name="category" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === "none" ? null : v)} value={field.value || "none"}>
                      <FormControl><SelectTrigger data-testid="select-order-category"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={orderForm.control} name="propertyId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property *</FormLabel>
                    <Select onValueChange={(v) => handlePropertyChange(v === "none" ? null : parseInt(v, 10))} value={field.value ? String(field.value) : "none"}>
                      <FormControl><SelectTrigger data-testid="select-order-property"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="none" disabled>Select a property</SelectItem>
                        {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField control={orderForm.control} name="requestedBy" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Requested By</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === "none" ? null : v)} value={field.value || "none"} disabled={!orderFormPropertyId}>
                      <FormControl><SelectTrigger data-testid="select-order-requested-by"><SelectValue placeholder="Select member" /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="none">No member</SelectItem>
                        {propertyMembers.map((m) => (
                          <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={orderForm.control} name="assigneeId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))} value={field.value ? String(field.value) : "none"}>
                      <FormControl><SelectTrigger data-testid="select-order-assignee"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {ownerAssigneeId != null && <SelectItem value={String(ownerAssigneeId)}>{ownerName} (Account owner)</SelectItem>}
                        {assignableTeamMembers.map((m) => <SelectItem key={m.id} value={String(m.inviteeUserId)}>{m.name || m.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField control={orderForm.control} name="quantity" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl><Input type="number" min={1} {...field} onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 1)} data-testid="input-order-quantity" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={orderForm.control} name="purchaseUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Link</FormLabel>
                    <FormControl><Input {...field} value={field.value || ""} placeholder="https://amazon.com/..." data-testid="input-order-purchase-url" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={orderForm.control} name="notes" render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl><Textarea {...field} value={field.value || ""} placeholder="Additional notes..." rows={2} data-testid="input-order-notes" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              </div>
              <div className="flex-shrink-0 pt-4 border-t mt-2">
                <Button type="submit" className="w-full" disabled={readOnly || (createOrderMutation.isPending)} data-testid="button-submit-order">
                  {createOrderMutation.isPending ? "Creating..." : "Create Supply Request"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={createItemOpen} onOpenChange={setCreateItemOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden" data-testid="dialog-create-supply-item">
          <DialogHeader className="flex-shrink-0"><DialogTitle>Add Supply Item</DialogTitle></DialogHeader>
          <Form {...itemForm}>
            <form onSubmit={itemForm.handleSubmit((data) => createItemMutation.mutate(data))} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <FormField control={itemForm.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl><Input {...field} placeholder="e.g. HVAC Filter 20x25" data-testid="input-item-name" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="grid grid-cols-2 gap-3">
                <FormField control={itemForm.control} name="category" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger data-testid="select-item-category"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={itemForm.control} name="preferredVendor" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Vendor</FormLabel>
                    <FormControl><Input {...field} value={field.value || ""} placeholder="e.g. Amazon, Home Depot" data-testid="input-item-vendor" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField control={itemForm.control} name="defaultQuantity" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Quantity</FormLabel>
                    <FormControl><Input type="number" min={1} {...field} onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 1)} data-testid="input-item-quantity" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={itemForm.control} name="isFrequent" render={({ field }) => (
                  <FormItem className="flex items-center gap-3 pt-6">
                    <FormControl><Switch checked={field.value ?? false} onCheckedChange={field.onChange} data-testid="switch-item-frequent" /></FormControl>
                    <FormLabel className="!mt-0">Frequent item</FormLabel>
                  </FormItem>
                )} />
              </div>
              <FormField control={itemForm.control} name="purchaseUrl" render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchase URL</FormLabel>
                  <FormControl><Input {...field} value={field.value || ""} placeholder="https://amazon.com/dp/..." data-testid="input-item-purchase-url" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              </div>
              <div className="flex-shrink-0 pt-4 border-t mt-2">
                <Button type="submit" className="w-full" disabled={readOnly || (createItemMutation.isPending)} data-testid="button-submit-item">
                  {createItemMutation.isPending ? "Adding..." : "Add Supply Item"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={createScheduleOpen} onOpenChange={setCreateScheduleOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden" data-testid="dialog-create-schedule">
          <DialogHeader className="flex-shrink-0"><DialogTitle>Add Recurring Schedule</DialogTitle></DialogHeader>
          <Form {...scheduleForm}>
            <form onSubmit={scheduleForm.handleSubmit((data) => createScheduleMutation.mutate(data))} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <FormField control={scheduleForm.control} name="supplyItemId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Supply Item</FormLabel>
                  <Select onValueChange={(v) => field.onChange(parseInt(v, 10))} value={field.value ? String(field.value) : ""}>
                    <FormControl><SelectTrigger data-testid="select-schedule-item"><SelectValue placeholder="Select item" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {items.map((i) => <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="grid grid-cols-2 gap-3">
                <FormField control={scheduleForm.control} name="propertyId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property *</FormLabel>
                    <Select onValueChange={(v) => field.onChange(parseInt(v, 10))} value={field.value ? String(field.value) : ""}>
                      <FormControl><SelectTrigger data-testid="select-schedule-property"><SelectValue placeholder="Select a property" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={scheduleForm.control} name="intervalDays" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interval (days)</FormLabel>
                    <FormControl><Input type="number" min={1} {...field} onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 30)} data-testid="input-schedule-interval" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={scheduleForm.control} name="nextDueDate" render={({ field }) => (
                <FormItem>
                  <FormLabel>Next Due Date</FormLabel>
                  <FormControl><Input type="date" {...field} value={field.value || ""} data-testid="input-schedule-next-due" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              </div>
              <div className="flex-shrink-0 pt-4 border-t mt-2">
                <Button type="submit" className="w-full" disabled={readOnly || (createScheduleMutation.isPending)} data-testid="button-submit-schedule">
                  {createScheduleMutation.isPending ? "Creating..." : "Create Schedule"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open) { setSelectedOrder(null); setMarkingOrdered(false); setMarkingDelivered(false); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden" data-testid="dialog-order-detail">
          <DialogHeader className="flex-shrink-0"><DialogTitle>Supply Order Detail</DialogTitle></DialogHeader>
          {selectedOrder && (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Item</p>
                  <p className="font-medium" data-testid="text-detail-item-name">{selectedOrder.itemName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={STATUS_COLORS[selectedOrder.status]} data-testid="badge-detail-status">{selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}</Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Requested</p>
                  <p className="text-sm">{format(new Date(selectedOrder.dateRequested), "MMM d, yyyy")}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Quantity</p>
                  <p className="text-sm">{selectedOrder.quantity}</p>
                </div>
                {selectedOrder.dateOrdered && (
                  <div>
                    <p className="text-xs text-gray-500">Ordered</p>
                    <p className="text-sm">{format(new Date(selectedOrder.dateOrdered), "MMM d, yyyy")}</p>
                  </div>
                )}
                {selectedOrder.dateDelivered && (
                  <div>
                    <p className="text-xs text-gray-500">Delivered</p>
                    <p className="text-sm">{format(new Date(selectedOrder.dateDelivered), "MMM d, yyyy")}</p>
                  </div>
                )}
              </div>
              {selectedOrder.purchaseUrl && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Purchase Link</p>
                  <a href={(() => { const clean = selectedOrder.purchaseUrl.startsWith("http") ? selectedOrder.purchaseUrl : `https://${selectedOrder.purchaseUrl}`; return buildAffiliateLink(clean) ?? clean; })()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline" data-testid="link-detail-purchase">
                    <ExternalLink className="h-4 w-4" />
                    {selectedOrder.purchaseUrl}
                  </a>
                </div>
              )}
              {selectedOrder.notes && (
                <div>
                  <p className="text-xs text-gray-500">Notes</p>
                  <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}
              {selectedOrder.orderReference && (
                <div>
                  <p className="text-xs text-gray-500">Order Number / Link</p>
                  {selectedOrder.orderReference.startsWith("http") ? (
                    <a href={selectedOrder.orderReference} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid="link-detail-order-ref">
                      <ExternalLink className="h-4 w-4" />
                      {selectedOrder.orderReference}
                    </a>
                  ) : (
                    <p className="text-sm font-medium" data-testid="text-detail-order-ref">{selectedOrder.orderReference}</p>
                  )}
                </div>
              )}
              {selectedOrder.trackingUrl && (
                <div>
                  <p className="text-xs text-gray-500">Tracking</p>
                  <a href={selectedOrder.trackingUrl.startsWith("http") ? selectedOrder.trackingUrl : `https://${selectedOrder.trackingUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid="link-detail-tracking">
                    <ExternalLink className="h-4 w-4" />
                    {selectedOrder.trackingUrl}
                  </a>
                </div>
              )}
            </div>
          )}
          {selectedOrder && (
            <div className="flex-shrink-0 pt-4 border-t mt-2 space-y-3">
                {selectedOrder.status === "requested" && !markingOrdered && (
                  <div className="flex items-center gap-2">
                    <Button disabled={readOnly} size="sm" onClick={() => { setMarkingOrdered(true); setMarkOrderedDate(format(new Date(), "yyyy-MM-dd")); setMarkOrderedReference(""); setMarkTrackingUrl(""); }} data-testid="button-mark-ordered">
                      Mark as Ordered
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="outline" onClick={() => { setEditingOrder(selectedOrder); setSelectedOrder(null); }} data-testid="button-edit-order">
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="destructive" onClick={() => deleteOrderMutation.mutate(selectedOrder.id)} data-testid="button-delete-order">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
                {selectedOrder.status === "requested" && markingOrdered && (() => {
                  const shipProperty = selectedOrder.propertyId ? properties.find((p) => p.id === selectedOrder.propertyId) : null;
                  return (
                  <div className="space-y-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3" data-testid="form-mark-ordered">
                    <p className="text-sm font-medium">Mark as Ordered</p>
                    {shipProperty?.address && (
                      <div className="bg-white dark:bg-gray-700 rounded border p-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
                          <MapPin className="h-3 w-3" />
                          Ship To
                        </div>
                        <p className="text-sm font-medium" data-testid="text-ship-to-address">{shipProperty.address}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-gray-500">Date Ordered</Label>
                      <Input type="date" value={markOrderedDate} onChange={(e) => setMarkOrderedDate(e.target.value)} data-testid="input-ordered-date" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Order Number or Order Link</Label>
                      <Input placeholder="Order #12345 or https://amazon.com/orders/..." value={markOrderedReference} onChange={(e) => setMarkOrderedReference(e.target.value)} data-testid="input-ordered-reference" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Tracking Link (optional)</Label>
                      <Input placeholder="https://ups.com/track/..." value={markTrackingUrl} onChange={(e) => setMarkTrackingUrl(e.target.value)} data-testid="input-ordered-tracking" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={readOnly || (updateOrderMutation.isPending)} onClick={() => {
                        updateOrderMutation.mutate({ id: selectedOrder.id, data: { status: "ordered", dateOrdered: new Date(markOrderedDate + "T12:00:00").toISOString(), orderReference: markOrderedReference || null, trackingUrl: markTrackingUrl || null } }, {
                          onSuccess: () => setMarkingOrdered(false)
                        });
                      }} data-testid="button-confirm-ordered">
                        {updateOrderMutation.isPending ? "Saving..." : "Confirm Ordered"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMarkingOrdered(false)} data-testid="button-cancel-ordered">Cancel</Button>
                    </div>
                  </div>
                  );
                })()}
                {selectedOrder.status === "ordered" && !markingDelivered && (
                  <div className="flex items-center gap-2">
                    <Button disabled={readOnly} size="sm" onClick={() => { setMarkingDelivered(true); setMarkDeliveredDate(format(new Date(), "yyyy-MM-dd")); setMarkDeliveredTracking(selectedOrder.trackingUrl || ""); }} data-testid="button-mark-delivered">
                      Mark as Delivered
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="outline" onClick={() => { setEditingOrder(selectedOrder); setSelectedOrder(null); }} data-testid="button-edit-order">
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="destructive" onClick={() => deleteOrderMutation.mutate(selectedOrder.id)} data-testid="button-delete-order">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
                {selectedOrder.status === "ordered" && markingDelivered && (
                  <div className="space-y-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3" data-testid="form-mark-delivered">
                    <p className="text-sm font-medium">Mark as Delivered</p>
                    <div>
                      <Label className="text-xs text-gray-500">Date Delivered</Label>
                      <Input type="date" value={markDeliveredDate} onChange={(e) => setMarkDeliveredDate(e.target.value)} data-testid="input-delivered-date" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Tracking Link (optional)</Label>
                      <Input placeholder="https://ups.com/track/..." value={markDeliveredTracking} onChange={(e) => setMarkDeliveredTracking(e.target.value)} data-testid="input-delivered-tracking" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={readOnly || (updateOrderMutation.isPending)} onClick={() => {
                        updateOrderMutation.mutate({ id: selectedOrder.id, data: { status: "delivered", dateDelivered: new Date(markDeliveredDate + "T12:00:00").toISOString(), trackingUrl: markDeliveredTracking || selectedOrder.trackingUrl || null } }, {
                          onSuccess: () => setMarkingDelivered(false)
                        });
                      }} data-testid="button-confirm-delivered">
                        {updateOrderMutation.isPending ? "Saving..." : "Confirm Delivered"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMarkingDelivered(false)} data-testid="button-cancel-delivered">Cancel</Button>
                    </div>
                  </div>
                )}
                {selectedOrder.status === "delivered" && (
                  <div className="flex items-center gap-2">
                    <Button disabled={readOnly} size="sm" variant="destructive" onClick={() => deleteOrderMutation.mutate(selectedOrder.id)} data-testid="button-delete-order">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingOrder} onOpenChange={(open) => { if (!open) setEditingOrder(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-edit-order">
          <DialogHeader><DialogTitle>Edit Supply Order</DialogTitle></DialogHeader>
          {editingOrder && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">Item Name</Label>
                <Input value={editingOrder.itemName} onChange={(e) => setEditingOrder({ ...editingOrder, itemName: e.target.value })} data-testid="input-edit-order-item" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Category</Label>
                  <Select value={editingOrder.category || "none"} onValueChange={(v) => setEditingOrder({ ...editingOrder, category: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="select-edit-order-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Property *</Label>
                  <Select value={editingOrder.propertyId ? String(editingOrder.propertyId) : "none"} onValueChange={(v) => handleEditPropertyChange(v === "none" ? null : parseInt(v, 10))}>
                    <SelectTrigger data-testid="select-edit-order-property"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Select a property</SelectItem>
                      {properties.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Quantity</Label>
                  <Input type="number" min={1} value={editingOrder.quantity || 1} onChange={(e) => setEditingOrder({ ...editingOrder, quantity: parseInt(e.target.value, 10) || 1 })} data-testid="input-edit-order-quantity" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Assign To</Label>
                  <Select value={editingOrder.assigneeId ? String(editingOrder.assigneeId) : "none"} onValueChange={(v) => setEditingOrder({ ...editingOrder, assigneeId: v === "none" ? null : parseInt(v, 10) })}>
                    <SelectTrigger data-testid="select-edit-order-assignee"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {ownerAssigneeId != null && <SelectItem value={String(ownerAssigneeId)}>{ownerName} (Account owner)</SelectItem>}
                      {assignableTeamMembers.map((m) => <SelectItem key={m.id} value={String(m.inviteeUserId)}>{m.name || m.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Purchase Link</Label>
                <Input value={editingOrder.purchaseUrl || ""} onChange={(e) => setEditingOrder({ ...editingOrder, purchaseUrl: e.target.value || null })} placeholder="https://..." data-testid="input-edit-order-purchase-url" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Notes</Label>
                <Textarea value={editingOrder.notes || ""} onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value || null })} rows={2} data-testid="input-edit-order-notes" />
              </div>
              <Button className="w-full" disabled={readOnly || (updateOrderMutation.isPending)} onClick={() => {
                const { id, userId, createdAt, dateRequested, ...rest } = editingOrder;
                updateOrderMutation.mutate({ id, data: { itemName: rest.itemName, category: rest.category, propertyId: rest.propertyId, quantity: rest.quantity, assigneeId: rest.assigneeId, purchaseUrl: rest.purchaseUrl, notes: rest.notes, requestedBy: rest.requestedBy } }, {
                  onSuccess: () => { setEditingOrder(null); }
                });
              }} data-testid="button-save-edit-order">
                {updateOrderMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-edit-item">
          <DialogHeader><DialogTitle>Edit Supply Item</DialogTitle></DialogHeader>
          {editingItem && (
            <EditItemForm item={editingItem} onSave={(data) => updateItemMutation.mutate({ id: editingItem.id, data })} isPending={updateItemMutation.isPending} categories={allCategories} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletingItem} onOpenChange={(open) => { if (!open && !deleteItemMutation.isPending) setDeletingItem(null); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-confirm-delete-item">
          <DialogHeader><DialogTitle>Delete {deletingItem?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">This will remove the item from your catalog. Existing order history is preserved.</p>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" disabled={deleteItemMutation.isPending} onClick={() => setDeletingItem(null)} data-testid="button-cancel-delete-item">Cancel</Button>
              <Button variant="destructive" className="flex-1" disabled={readOnly || deleteItemMutation.isPending} onClick={() => { if (deletingItem) deleteItemMutation.mutate(deletingItem.id); }} data-testid="button-confirm-delete-item">
                {deleteItemMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyItemId !== null} onOpenChange={(open) => { if (!open) { setHistoryItemId(null); setHistoryPropertyId(null); } }}>
        <DialogContent className="max-w-lg" data-testid="dialog-order-history">
          <DialogHeader><DialogTitle>Order History & Analytics</DialogTitle></DialogHeader>
          {historyQuery.isLoading ? (
            <div className="py-8 text-center text-gray-400">Loading history...</div>
          ) : historyQuery.data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900" data-testid="text-total-orders">{historyQuery.data.totalOrders}</p>
                  <p className="text-xs text-gray-500">Total Orders</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900" data-testid="text-avg-interval">{historyQuery.data.avgIntervalDays ?? "—"}</p>
                  <p className="text-xs text-gray-500">Avg Days Between</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${historyQuery.data.anomaly ? "bg-red-50" : "bg-gray-50"}`}>
                  {historyQuery.data.anomaly ? (
                    <>
                      <AlertTriangle className="h-6 w-6 text-red-600 mx-auto" data-testid="icon-anomaly" />
                      <p className="text-xs text-red-600 font-medium mt-1">Unusually Fast</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-green-700">OK</p>
                      <p className="text-xs text-gray-500">Normal Rate</p>
                    </>
                  )}
                </div>
              </div>
              {historyQuery.data.orders.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Last 5 Orders</h4>
                  <div className="space-y-2">
                    {historyQuery.data.orders.map((order: SupplyOrder) => (
                      <div key={order.id} className="flex items-center justify-between text-sm border-b pb-2" data-testid={`row-history-order-${order.id}`}>
                        <div>
                          <Badge className={`text-xs ${STATUS_COLORS[order.status]}`}>{order.status}</Badge>
                          <span className="ml-2 text-gray-600">Qty: {order.quantity}</span>
                        </div>
                        <span className="text-gray-500 text-xs">{format(new Date(order.dateRequested), "MMM d, yyyy")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400">No history available</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditItemForm({ item, onSave, isPending, categories }: { item: SupplyItem; onSave: (data: Partial<SupplyItem>) => void; isPending: boolean; categories: string[] }) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const form = useForm({
    defaultValues: {
      name: item.name,
      category: item.category,
      purchaseUrl: item.purchaseUrl || "",
      preferredVendor: item.preferredVendor || "",
      defaultQuantity: item.defaultQuantity || 1,
      isFrequent: item.isFrequent || false,
    },
  });

  return (
    <form onSubmit={form.handleSubmit((data) => onSave(data))} className="space-y-4">
      <div>
        <label className="text-sm font-medium">Name</label>
        <Input {...form.register("name")} data-testid="input-edit-item-name" />
      </div>
      <div>
        <label className="text-sm font-medium">Category</label>
        <select {...form.register("category")} className="w-full border rounded-md p-2 text-sm" data-testid="select-edit-item-category">
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-medium">Purchase URL</label>
        <Input {...form.register("purchaseUrl")} data-testid="input-edit-item-url" />
      </div>
      <div>
        <label className="text-sm font-medium">Preferred Vendor</label>
        <Input {...form.register("preferredVendor")} data-testid="input-edit-item-vendor" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium">Default Quantity</label>
          <Input type="number" min={1} {...form.register("defaultQuantity", { valueAsNumber: true })} data-testid="input-edit-item-qty" />
        </div>
        <div className="flex items-center gap-3 pt-6">
          <input type="checkbox" {...form.register("isFrequent")} data-testid="checkbox-edit-item-frequent" />
          <label className="text-sm">Frequent item</label>
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={readOnly || (isPending)} data-testid="button-save-edit-item">
        {isPending ? "Saving..." : "Save Changes"}
      </Button>
    </form>
  );
}
